import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";

// Free OpenStreetMap Nominatim geocoding — no API key. Biases toward Moscow, ID.
const MOSCOW_ID = { lat: 46.7324, lng: -116.9997 };

export default function AddressAutocomplete({ value, onChange, onSelect, placeholder = "Address", testid = "address-autocomplete-input", className = "h-11" }) {
  const [q, setQ] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const search = (text) => {
    if (!text || text.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    // Bias toward Moscow, ID: use viewbox + bounded=1 (prefer, not strict) + countrycodes=us
    // viewbox is a ~60-mile box around Moscow (lon_tl, lat_tl, lon_br, lat_br)
    const vb = "-117.8,47.2,-116.2,46.2";
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(text)}&format=json&limit=5&addressdetails=1&countrycodes=us&viewbox=${vb}&bounded=0`;
    fetch(url, { headers: { "Accept-Language": "en" } })
      .then((r) => r.json())
      .then((data) => {
        const list = data || [];
        // Sort client-side by distance from Moscow, ID for extra bias
        list.sort((a, b) => {
          const da = Math.hypot(parseFloat(a.lat) - MOSCOW_ID.lat, parseFloat(a.lon) - MOSCOW_ID.lng);
          const db = Math.hypot(parseFloat(b.lat) - MOSCOW_ID.lat, parseFloat(b.lon) - MOSCOW_ID.lng);
          return da - db;
        });
        setSuggestions(list);
      })
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  };

  const onInput = (e) => {
    const v = e.target.value;
    setQ(v);
    onChange?.(v);
    setOpen(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => search(v), 350);
  };

  const pick = (s) => {
    const display = s.display_name;
    setQ(display);
    setOpen(false);
    onChange?.(display);
    onSelect?.({
      display_name: display,
      latitude: parseFloat(s.lat),
      longitude: parseFloat(s.lon),
    });
  };

  return (
    <div className="relative" ref={boxRef}>
      <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
      <Input value={q} onChange={onInput} onFocus={() => q.length >= 3 && setOpen(true)} placeholder={placeholder} className={`pl-9 ${className}`} data-testid={testid} autoComplete="off" />
      {open && (suggestions.length > 0 || loading) && (
        <div className="absolute z-40 mt-1 w-full bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden">
          {loading && suggestions.length === 0 && (
            <div className="px-4 py-3 text-sm text-slate-500">Searching…</div>
          )}
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              onClick={() => pick(s)}
              data-testid={`address-suggestion-${s.place_id}`}
              className="w-full text-left px-4 py-2.5 hover:bg-slate-50 text-sm text-slate-700 border-b border-slate-100 last:border-b-0"
            >
              <div className="font-semibold text-slate-900 line-clamp-1">{s.display_name.split(",")[0]}</div>
              <div className="text-xs text-slate-500 line-clamp-1">{s.display_name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
